import { Fragment, useEffect, useContext, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from "../../utils/auth.js";
import { useHistory, useLocation } from 'react-router-dom';

import { get, ref, onValue } from "firebase/database";
import { db } from '../../utils/firebaseApp.js';

import axios from "axios";

import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid'
import { ExclamationTriangleIcon, DocumentDuplicateIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import { Search } from 'lucide-react'
import Pagination from './ExtractionFolder/Pagination.js';
import EmailUpload from './ExtractionFolder/EmailUpload.js';
import EmailHistory from './ExtractionFolder/EmailHistory.js';

import Breadcrumb from '../../components/Breadcrumb';

import ModalForNewExtraction from '../../components/utils/ModalForNewExtraction.js';
import CreateExtraction from '../../components/CreateExtraction.js';

import { Dialog, Transition } from '@headlessui/react'

import {
    PencilIcon,
} from '@heroicons/react/20/solid'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileExcel, faFileCsv, faCode, faRefresh, faEdit as IconEdit, faCopy as IconCopy } from '@fortawesome/free-solid-svg-icons'

import {
    GlobeAltIcon, // For extractionLanguage
    CalendarIcon, // For extractionCreatedAt
    CubeIcon, // For extractionBatchCount (alternative for CollectionIcon)
    AdjustmentsHorizontalIcon,
    CodeBracketIcon as CustomCodeIcon,
} from '@heroicons/react/24/outline'; // Import outline icons from Heroicons v2

import toast from 'react-hot-toast';

// const duplicateUsersId = ["WgVLxXREk7eW2aQeFOSfRbuxJbm1", "4qgLL4svWmQFGcseue0ZrrJUGfC3", "3fEgxedJEsfxRT1V4x4NLxgppbE2"];

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

function formatDate(dateString) {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(Number(dateString)).toLocaleDateString(undefined, options);
}

export default function ExtractionScreen() {
    const { currentUser } = useContext(AuthContext);
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const isEmailUploadEnabled = process.env.REACT_APP_EMAIL_UPLOAD_ENABLED === 'true';

    const adminEmails = ['barzoiu.alexandru@gmail.com', "alexraducu03@gmail.com"];
    const isAdmin = adminEmails.includes(currentUser.email);

    const { extractionId } = useParams();
    const [extraction, setExtraction] = useState({});

    const [allBatches, setAllBatches] = useState([]); // Store all batches for fake pagination
    const [displayedBatches, setDisplayedBatches] = useState([]); // Batches for the current page
    const [currentBatchPage, setCurrentBatchPage] = useState(1);
    const [itemsPerBatchPage, setItemsPerBatchPage] = useState(10);

    const history = useHistory();
    const location = useLocation();
    const hasSetInitialPage = useRef(false); // Flag to ensure the initial page is only set once

    const [editBatchModalOpen, setEditBatchModalOpen] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [isUpdatingBatch, setIsUpdatingBatch] = useState(false);

    const [adminUserId, setAdminUserId] = useState('');
    const [searchedUserId, setSearchedUserId] = useState('');
    const [adminBatchIdQuery, setAdminBatchIdQuery] = useState('');

    const [emailHistory, setEmailHistory] = useState([]);
    const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
    const [emailHistoryError, setEmailHistoryError] = useState(null);

    // Load admin user ID from URL on mount
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const adminUserIdParam = queryParams.get('adminUserId');
        
        if (isAdmin && adminUserIdParam) {
            setSearchedUserId(adminUserIdParam);
            setAdminUserId(adminUserIdParam);
        }
    }, [isAdmin, location.search]);

    // Real-time listener for all batches
    useEffect(() => {
        // Use searchedUserId if admin has searched, otherwise use current userId
        const targetUserId = (isAdmin && searchedUserId) ? searchedUserId : currentUser.uid;
        const userId = targetUserId;
        const batchesRef = ref(db, `batches/${userId}/${extractionId}`);

        const unsubscribe = onValue(batchesRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const tempBatches = Object.keys(data).map((key) => {
                    const batch = data[key];
                    return {
                        id: key,
                        name: batch.name ?? null,
                        numberOfDocuments: batch.files ? batch.files.length : 0,
                        startDateTime: batch.startTime,
                        status: batch.status,
                    };
                }).sort((a, b) => Number(b.startDateTime) - Number(a.startDateTime));

                setAllBatches(tempBatches);
            } else {
                setAllBatches([]); // Handle case where no batches exist
            }
        });

        return () => unsubscribe();
    }, [extractionId, currentUser.uid, searchedUserId, isAdmin]);

    // Set initial page from query param only after `allBatches` is populated
    useEffect(() => {
        if (allBatches.length > 0 && !hasSetInitialPage.current) {
            const queryParams = new URLSearchParams(location.search);
            let pageParam = parseInt(queryParams.get('page'), 10);
            const totalPages = Math.ceil(allBatches.length / itemsPerBatchPage);

            // Validate the pageParam and fall back to 1 if out of range or NaN
            if (isNaN(pageParam) || pageParam < 1 || pageParam > totalPages) {
                pageParam = 1;
                // Update the URL to reflect the adjusted page
                queryParams.set('page', pageParam);
                history.replace(`${location.pathname}?${queryParams.toString()}`);
            }

            setCurrentBatchPage(pageParam);
            hasSetInitialPage.current = true;
        }
    }, [allBatches, location.search, itemsPerBatchPage]);

    // Update displayedBatches when the page, items per page, or allBatches change
    useEffect(() => {
        const sourceBatches = (isAdmin && adminBatchIdQuery.trim())
            ? allBatches.filter((batch) =>
                batch.id.toLowerCase().includes(adminBatchIdQuery.trim().toLowerCase())
            )
            : allBatches;
        const start = (currentBatchPage - 1) * itemsPerBatchPage;
        const end = start + itemsPerBatchPage;
        setDisplayedBatches(sourceBatches.slice(start, end));
    }, [allBatches, currentBatchPage, itemsPerBatchPage, isAdmin, adminBatchIdQuery]);

    // Update query params when `currentBatchPage` changes
    useEffect(() => {
        if (hasSetInitialPage.current) {
            const queryParams = new URLSearchParams(location.search);
            queryParams.set('page', currentBatchPage);
            history.replace(`${location.pathname}?${queryParams.toString()}`);
        }
    }, [currentBatchPage, location.pathname]);

    // Pagination controls
    const nextBatchPage = () => {
        const totalBatches = (isAdmin && adminBatchIdQuery.trim())
            ? allBatches.filter((batch) =>
                batch.id.toLowerCase().includes(adminBatchIdQuery.trim().toLowerCase())
            ).length
            : allBatches.length;
        if (currentBatchPage < Math.ceil(totalBatches / itemsPerBatchPage)) {
            setCurrentBatchPage((prev) => prev + 1);
        }
    };

    const previousBatchPage = () => {
        if (currentBatchPage > 1) {
            setCurrentBatchPage((prev) => prev - 1);
        }
    };

    const selectBatchPage = (pageNumber) => {
        const totalBatches = (isAdmin && adminBatchIdQuery.trim())
            ? allBatches.filter((batch) =>
                batch.id.toLowerCase().includes(adminBatchIdQuery.trim().toLowerCase())
            ).length
            : allBatches.length;
        if (pageNumber >= 1 && pageNumber <= Math.ceil(totalBatches / itemsPerBatchPage)) {
            setCurrentBatchPage(pageNumber);
        }
    };

    const fetchExtraction = async () => {
        // Use searchedUserId if admin has searched, otherwise use current userId
        const targetUserId = (isAdmin && searchedUserId) ? searchedUserId : currentUser.uid;
        let userId = targetUserId;
        const databaseRef = ref(db, 'extractions/' + userId + '/' + extractionId);
        try {
            const snapshot = await get(databaseRef);
            if (snapshot.exists()) {
                const extractionData = snapshot.val();
                setExtraction(extractionData);
            }
        } catch (error) {
        }
    };

    const isImpersonating = isAdmin && searchedUserId && searchedUserId !== currentUser.uid;

    const fetchEmailHistory = useCallback(async () => {
        if (isImpersonating || !isEmailUploadEnabled) {
            setEmailHistory([]);
            setEmailHistoryError(null);
            return;
        }

        setEmailHistoryLoading(true);
        setEmailHistoryError(null);
        try {
            const idToken = await currentUser.getIdToken();
            const response = await axios.post(`${serverUrl}/inboundEmail/history`, {
                extractionId,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    Authorization: `Bearer ${idToken}`,
                },
            });
            setEmailHistory(Array.isArray(response.data?.emails) ? response.data.emails : []);
        } catch (error) {
            const data = error?.response?.data;
            const message = (typeof data === 'string' && data.trim())
                ? data
                : (data?.message || error?.message || 'Unable to load email history');
            setEmailHistoryError(message);
            setEmailHistory([]);
        } finally {
            setEmailHistoryLoading(false);
        }
    }, [currentUser, extractionId, isEmailUploadEnabled, isImpersonating, serverUrl]);

    useEffect(() => {
        fetchExtraction();
    }, [extractionId, searchedUserId, isAdmin]);

    useEffect(() => {
        fetchEmailHistory();
    }, [fetchEmailHistory]);

    const pages = [
        { 
            name: extraction.name, 
            href: searchedUserId 
                ? `/extractions/${extractionId}?adminUserId=${searchedUserId}`
                : `/extractions/${extractionId}`, 
            current: true 
        },
    ]

    const hiddenLinkRef = useRef(null);

    const handleRowClick = (dynamicUrl) => {
        // Preserve adminUserId in navigation
        let finalUrl = dynamicUrl;
        if (searchedUserId) {
            finalUrl = `${dynamicUrl}?adminUserId=${searchedUserId}`;
        }
        hiddenLinkRef.current.href = finalUrl;
        hiddenLinkRef.current.click();
    };

    const statuses = {
        Complete: 'text-green-700 bg-green-50 ring-green-600/20',
        finished: 'text-green-700 bg-green-50 ring-green-600/20',
        'In progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
        processing: 'text-gray-600 bg-gray-50 ring-gray-500/10',
        Archived: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
    }

    const [open, setOpen] = useState(false);
    const [open2, setOpen2] = useState(false);

    const deleteExtraction = () => {
        currentUser.getIdToken().then((idToken) => {
            const url = serverUrl + "/delete";

            axios.post(url, {
                "extractionId": extractionId,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then((response) => {
                if (response.status === 200) {
                    console.log("hey merge");
                    window.location.href = "/extractions";
                } else {
                }
            }).catch((error) => {
            });
        });
    }

    const [showDelete, setShowDelete] = useState(false);

    const handleDelete = async () => {
        setShowDelete(true);
    }

    // create a function that takes the extractionId, batchId and a name and calls the backend to update the batch name
    const updateBatchName = async (batchId, name) => {
        try {
            return await currentUser.getIdToken().then(async (idToken) => {
                const url = serverUrl + "/updateBatchName";

                const response = await axios.post(url, {
                    extractionId: extractionId,
                    batchId: batchId,
                    name: name
                }, {
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Origin": "*",
                        Authorization: `Bearer ${idToken}`
                    }
                });

                if (response.status === 200) {
                    toast.success('Batch details updated successfully!');
                    return true;
                } else {
                    toast.error('Unable to update batch details!');
                    return false;
                }
            });
        } catch (error) {
            toast.error('Unable to update batch details!');
            return false;
        }
    }

    const duplicateExtraction = async () => {
        try {
            await currentUser.getIdToken().then(async (idToken) => {
                const url = serverUrl + "/duplicateExtraction";

                const response = await axios.post(url, {
                    extractionId: extractionId,
                }, {
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Origin": "*",
                        Authorization: `Bearer ${idToken}`
                    }
                });

                if (response.status === 200) {
                    toast.success('Extraction duplicated successfully!');
                    return true;
                } else {
                    toast.error('Unable to duplicate extraction!');
                    return false;
                }
            });
        } catch (error) {
            toast.error('Unable to duplicate extraction!');
        }
    }

    const [showDuplicate, setShowDuplicate] = useState(false);
    const environment = process.env.REACT_APP_ENV;

    const handleAdminSearch = () => {
        if (adminUserId.trim()) {
            setSearchedUserId(adminUserId.trim());
            // Add adminUserId to URL
            const queryParams = new URLSearchParams(location.search);
            queryParams.set('adminUserId', adminUserId.trim());
            history.replace(`${location.pathname}?${queryParams.toString()}`);
        }
    };

    const handleAdminSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAdminSearch();
        }
    };

    const handleClearSearch = () => {
        setAdminUserId('');
        setSearchedUserId('');
        setAdminBatchIdQuery('');
        // Remove adminUserId from URL
        const queryParams = new URLSearchParams(location.search);
        queryParams.delete('adminUserId');
        const newUrl = queryParams.toString()
            ? `${location.pathname}?${queryParams.toString()}`
            : location.pathname;
        history.replace(newUrl);
    };

    useEffect(() => {
        if (isAdmin && adminBatchIdQuery.trim()) {
            setCurrentBatchPage(1);
        }
    }, [adminBatchIdQuery, isAdmin]);

    const filteredBatches = (isAdmin && adminBatchIdQuery.trim())
        ? allBatches.filter((batch) =>
            batch.id.toLowerCase().includes(adminBatchIdQuery.trim().toLowerCase())
        )
        : allBatches;

    return (
        <div className="">
            <Breadcrumb 
                pages={pages} 
                currentHref={searchedUserId ? `/extractions?adminUserId=${searchedUserId}` : "/extractions"} 
            />
            <a ref={hiddenLinkRef} href="https://new-link.com" style={{ display: 'none' }}>a</a>

            <div className="">
                <ExtractionHeading
                    extractionId={extractionId}
                    extractionName={extraction.name}
                    extractionLanguage={extraction.language}
                    extractionCreatedAt={formatDate(extraction.createdAt)}
                    extractionBatchCount={allBatches.length}
                    extractionFieldsCount={extraction.fields?.length || 0}
                    extractionWorkflowId={extraction.workflowId}
                    editExtraction={() => setOpen2(true)}
                    deleteExtraction={() => handleDelete()}
                    duplicateExtraction={() => setShowDuplicate(true)}
                    // canDuplicate={duplicateUsersId.includes(currentUser.uid)}
                    canDuplicate={true}
                />
            </div>

            {/* Admin Search */}
            {isAdmin && (
                <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                            Admin
                        </span>
                        <h3 className="text-sm font-semibold text-indigo-900">View batches for any user</h3>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                            type="text"
                            value={adminUserId}
                            onChange={(e) => setAdminUserId(e.target.value)}
                            onKeyPress={handleAdminSearchKeyPress}
                            placeholder="Enter User ID"
                            className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            onClick={handleAdminSearch}
                            disabled={!adminUserId.trim()}
                            className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="h-4 w-4" />
                            Search
                        </button>
                        {searchedUserId && (
                            <button
                                onClick={handleClearSearch}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {searchedUserId && (
                        <p className="mt-2 text-sm text-indigo-700">
                            Viewing batches for user: <span className="font-mono font-semibold">{searchedUserId}</span>
                        </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <label htmlFor="admin-batch-id-search" className="text-sm font-medium text-indigo-900 whitespace-nowrap">
                            Filter by Batch ID
                        </label>
                        <input
                            id="admin-batch-id-search"
                            type="text"
                            value={adminBatchIdQuery}
                            onChange={(e) => setAdminBatchIdQuery(e.target.value)}
                            placeholder="Enter batch ID"
                            className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {adminBatchIdQuery.trim() && (
                            <button
                                onClick={() => setAdminBatchIdQuery('')}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="sm:flex sm:items-center mt-7">
                <div className="sm:flex-auto">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">Batches</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        A list of all the batches that have been uploaded to the extraction.
                    </p>
                </div>
                <div className="mt-4 flex items-center gap-2 sm:ml-16 sm:mt-0 sm:flex-none">
                    {!isImpersonating && isEmailUploadEnabled && (
                        <EmailUpload
                            extractionId={extractionId}
                            inboundEmail={extraction.inboundEmail ?? null}
                            onUpdated={fetchExtraction}
                        />
                    )}
                    <button
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                        Add files
                    </button>
                </div>
            </div>

            <ModalForNewExtraction open={open} setOpen={setOpen} close={() => { }}>
                {/* new batch */}
                <CreateExtraction closeModal={() => {
                    setOpen(false);
                }} />
            </ModalForNewExtraction>

            <ModalForNewExtraction open={open2} setOpen={setOpen2} close={() => { }}>
                {/* edit extraction */}
                <CreateExtraction isEdit={true} closeModal={() => {
                    setOpen2(false);
                    fetchExtraction();
                }} />
            </ModalForNewExtraction>

            <ShowDeleteModal open={showDelete} setOpen={setShowDelete} deleteFunction={() => {
                deleteExtraction();
                setShowDelete(false);
            }} />

            <ShowDuplicateModal
                open={showDuplicate}
                setOpen={setShowDuplicate}
                duplicateFunction={async () => {
                    await duplicateExtraction();
                    setShowDuplicate(false);
                }}
            />

            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                            Id
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Name
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            No. of documents
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Created at
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {displayedBatches.map((batch) => (
                                        <tr key={batch.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick("/extractions/" + extractionId + "/batch/" + batch.id)}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                {batch.id}
                                                {environment === "development" && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(batch.id);
                                                            toast.success('Batch ID copied to clipboard!');
                                                        }}
                                                        className="ml-2 text-indigo-400 hover:text-indigo-500"
                                                    >
                                                        <FontAwesomeIcon icon={IconCopy} className="h-4 w-4" aria-hidden="true" />
                                                    </button>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    {batch.name ? (
                                                        <>
                                                            <span>{batch.name}</span>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedBatch(batch);
                                                                    setEditBatchModalOpen(true);
                                                                }}
                                                                className="ml-2 text-indigo-400 hover:text-indigo-500"
                                                            >
                                                                <FontAwesomeIcon icon={IconEdit} className="h-4 w-4" aria-hidden="true" />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedBatch(batch);
                                                                setEditBatchModalOpen(true);
                                                            }}
                                                            className="text-indigo-400 hover:text-indigo-500"
                                                        >
                                                            <FontAwesomeIcon icon={IconEdit} className="h-4 w-4" aria-hidden="true" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{batch.numberOfDocuments} {batch.numberOfDocuments === 1 ? 'document' : 'documents'}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(batch.startDateTime)}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <p
                                                    className={classNames(
                                                        statuses[batch.status],
                                                        'rounded-md inline-block mt-0.5 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
                                                    )}
                                                >
                                                    {batch.status}
                                                </p>
                                            </td>
                                            {/* <td className="whitespace-nowrap py-4 pl-3 pr-4 text-center text-sm font-medium sm:pr-3 relative">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent row click when clicking edit
                                                        setSelectedBatch(batch);
                                                        setEditBatchModalOpen(true);
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    Edit
                                                </button>
                                            </td> */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {displayedBatches.length <= 0 &&
                            <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-12">
                                <div className="text-center">
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {adminBatchIdQuery.trim() ? 'No batches found' : 'No files yet'}
                                    </h2>
                                    <p className="mt-1.5 text-sm text-gray-600">
                                        {adminBatchIdQuery.trim()
                                            ? 'Try a different batch ID or clear the filter.'
                                            : 'Create a new batch and upload files.'
                                        }
                                    </p>
                                    <button
                                        onClick={() => setOpen(true)}
                                        className="mt-3 ml-2 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    >
                                        <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                        Add files
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

            {filteredBatches.length > 0 && (
                <Pagination
                    numberOfPages={Math.ceil(filteredBatches.length / itemsPerBatchPage)}
                    currentPageNumber={currentBatchPage}
                    previousButton={previousBatchPage}
                    nextButton={nextBatchPage}
                    selectPage={selectBatchPage}
                    itemsPerPage={itemsPerBatchPage}
                    setItemsPerPage={setItemsPerBatchPage}
                />
            )}

            {!isImpersonating && isEmailUploadEnabled && extraction.inboundEmail && (
                <div className="mt-10">
                    <div className="sm:flex sm:items-center">
                        <div className="sm:flex-auto">
                            <h3 className="text-base font-semibold leading-6 text-gray-900">Email history</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Emails received at this extraction&apos;s upload address, including rejected senders and failed uploads.
                            </p>
                        </div>
                    </div>
                    <div className="mt-4">
                        <EmailHistory
                            emails={emailHistory}
                            isLoading={emailHistoryLoading}
                            error={emailHistoryError}
                            onRefresh={fetchEmailHistory}
                            extractionId={extractionId}
                            emptyMessage="No emails have arrived at this address yet."
                        />
                    </div>
                </div>
            )}

            {/* Edit Batch Modal */}
            <EditBatchModal
                open={editBatchModalOpen}
                setOpen={setEditBatchModalOpen}
                batchName={selectedBatch?.name}
                loading={isUpdatingBatch}
                onSave={async (newName) => {
                    try {
                        setIsUpdatingBatch(true);
                        await updateBatchName(selectedBatch.id, newName);
                    } catch (error) {
                        toast.error('Unable to update batch details!');
                    } finally {
                        setIsUpdatingBatch(false);
                        setEditBatchModalOpen(false); // Close modal regardless of success/failure
                    }
                }}
            />
        </div>
    )
}

function ExtractionHeading({
    extractionId,
    extractionName,
    extractionLanguage,
    extractionCreatedAt,
    extractionBatchCount,
    extractionFieldsCount,
    extractionWorkflowId,
    editExtraction,
    deleteExtraction,
    duplicateExtraction,
    canDuplicate,
}) {
    const environment = process.env.REACT_APP_ENV;

    const metaItemClass = "inline-flex items-center gap-1.5";
    const metaIconClass = "h-4 w-4 flex-shrink-0 text-gray-400";
    const iconButtonClass = "inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 hover:text-gray-900";

    return (
        <div className="border-b border-gray-200 pb-5">
            <div className="lg:flex lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-8 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        {extractionName}
                    </h2>

                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-gray-500">
                        {extractionLanguage && (
                            <span className={metaItemClass}>
                                <GlobeAltIcon className={metaIconClass} aria-hidden="true" />
                                {extractionLanguage}
                            </span>
                        )}
                        <span className={metaItemClass}>
                            <CubeIcon className={metaIconClass} aria-hidden="true" />
                            {extractionBatchCount} {extractionBatchCount === 1 ? 'batch' : 'batches'}
                        </span>
                        <span className={metaItemClass}>
                            <AdjustmentsHorizontalIcon className={metaIconClass} aria-hidden="true" />
                            {extractionFieldsCount} {extractionFieldsCount === 1 ? 'field' : 'fields'}
                        </span>
                        <span className={metaItemClass}>
                            <CalendarIcon className={metaIconClass} aria-hidden="true" />
                            {extractionCreatedAt}
                        </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-500 ring-1 ring-inset ring-gray-200">
                            <CustomCodeIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                            <span className="truncate font-mono text-gray-700">{extractionId}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText(extractionId);
                                    toast.success('Extraction ID copied to clipboard!');
                                }}
                                title="Copy extraction ID"
                                aria-label="Copy extraction ID"
                                className="flex-shrink-0 text-gray-400 transition-colors hover:text-indigo-600"
                            >
                                <ClipboardIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                        </span>

                        {environment === "development" && extractionWorkflowId && (
                            <span className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-500 ring-1 ring-inset ring-gray-200">
                                Workflow
                                <span className="truncate font-mono text-gray-700">{extractionWorkflowId}</span>
                            </span>
                        )}
                    </div>
                </div>

                <div className="mt-5 flex items-center gap-2 lg:ml-6 lg:mt-1 lg:flex-shrink-0">
                    <button
                        onClick={() => editExtraction()}
                        type="button"
                        className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        <PencilIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                        Edit extraction
                    </button>

                    <div className="mx-1 h-6 w-px bg-gray-200" aria-hidden="true" />

                    {canDuplicate && (
                        <button
                            onClick={() => duplicateExtraction()}
                            type="button"
                            title="Duplicate extraction"
                            aria-label="Duplicate extraction"
                            className={iconButtonClass}
                        >
                            <DocumentDuplicateIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    )}

                    <button
                        onClick={() => deleteExtraction()}
                        type="button"
                        title="Delete extraction"
                        aria-label="Delete extraction"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 transition-colors hover:bg-red-50 hover:text-red-600 hover:ring-red-200"
                    >
                        <TrashIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function ShowDeleteModal({ open, setOpen, deleteFunction }) {
    const cancelButtonRef = useRef(null)

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Confirm Deletion
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to delete this extraction? This action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                        onClick={() => deleteFunction()}
                                    >
                                        Delete
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={() => setOpen(false)}
                                        ref={cancelButtonRef}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

function ShowDuplicateModal({ open, setOpen, duplicateFunction }) {
    const cancelButtonRef = useRef(null)

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <DocumentDuplicateIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Duplicate Extraction
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to duplicate this extraction? This will create a new copy with all the same settings.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                        onClick={() => duplicateFunction()}
                                    >
                                        Duplicate
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={() => setOpen(false)}
                                        ref={cancelButtonRef}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

function EditBatchModal({
    open,
    setOpen,
    batchName,
    onSave,
    loading
}) {
    const cancelButtonRef = useRef(null);
    const [name, setName] = useState(batchName || '');

    useEffect(() => {
        if (open) {
            setName(batchName || '');
        }
    }, [open, batchName]);

    const handleInputChange = (e) => {
        setName(e.target.value);
    };

    const saveFunction = async () => {
        await onSave(name);
        clearFunction();
    };

    const clearFunction = () => {
        setName('');
    };

    const cancelFunction = () => {
        clearFunction();
        setOpen(false);
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-100" initialFocus={cancelButtonRef} onClose={() => setOpen(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                                                Edit Batch
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Update the batch name to help you better organize and identify your document sets.
                                                </p>
                                            </div>

                                            <form className="mt-3.5 mb-1">
                                                <div className="w-full">
                                                    <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                                                        Batch Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        id="name"
                                                        value={name}
                                                        onChange={handleInputChange}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200"
                                                        placeholder="Enter batch name"
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        onClick={() => saveFunction()}
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={loading}
                                    >
                                        Save changes
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => cancelFunction()}
                                        ref={cancelButtonRef}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
